<script>
  // @ts-nocheck

  import { getContext, createEventDispatcher } from "svelte";
  import { objectify } from "overline";
  import { Dimension } from "dimtext";
  import cloneDeep from "lodash/cloneDeep";
  import isEmpty from "lodash/isEmpty";
  import isEqual from "lodash/isEqual";
  import set from "lodash/set";
  import pick from "lodash/pick";
  import {
    hydrateItem,
    createType as defaultType,
    createItem as defaultItem,
    hydrateType,
    hydrateMaterial,
  } from "@local/lamina-core";
  import liteDrawing from "@local/extensions/drawing/lite-drawing.js";
  import materialDrawing from "@local/extensions/drawing/material-drawing.js";
  import { typeColorTrue } from "@local/extensions/drawing/type-color.js";

  import CaretLeftIcon from "@local/assets/icons/caret-left.svg";
  import { Modal } from "svelte-utilities";
  import FilterInput from "#lib/products/FilterInput.svelte";
  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import MultiSelectInput from "#lib/sidebar/MultiSelectInput.svelte";
  import ProductInput from "#lib/sidebar/ProductInput.svelte";
  import ColorInput from "#lib/sidebar/ColorInput.svelte";
  import LibrarySection from "#lib/sidebar/LibrarySection.svelte";
  import MakeupThumbnail from "#lib/MakeupThumbnail.svelte";
  import DimtextInput from "./sidebar/DimtextInput.svelte";
  import TextInput from "./sidebar/TextInput.svelte";
  import BooleanInput from "./sidebar/BooleanInput.svelte";
  import IntegerInput from "./sidebar/IntegerInput.svelte";
  import Viewport from "#lib/drawing/ViewportFixed.svelte";
  import ViewportMinimal from "#lib/drawing/ViewportMinimal.svelte";

  import { recordFilter } from "#src/extensions/makeup-library.js";
  import imageUrl from "#src/extensions/image-url.js";
  import productSpecs from "#src/extensions/product-specs.js";
  import { parseItemScript, parseItemParams } from "#src/extensions/importers/item-script.js";
  import { profile } from "#src/stores/auth.js";
  import { api } from "#src/api";

  export let group;
  export let types;
  export let items;
  export const open = (prop) => openModal(prop);

  const dispatch = createEventDispatcher();

  let modal;
  let category = null;
  let editing = false;
  let editedTypeId = null;
  let editedItemId = null;
  let filter = "";

  let product = null; // The new "types" record
  let itemProductEntry; // The record in product_entries for the new item
  let newItem; // The new item to be created

  let specs = [];
  let specOptions = [];
  let params = [];

  let selectedProductId; // Used to highlight the selected product/category
  let selectedCategory = null;

  const makeupLibrary = getContext("makeupLibrary");
  const suppliers = getContext("suppliers");
  const supplier = getContext("supplier");

  const inputs = {
    text: TextInput,
    boolean: BooleanInput,
    dimtext: DimtextInput,
    select: SelectInput,
    multiselect: MultiSelectInput,
    integer: IntegerInput,
    product: ProductInput,
    color: ColorInput,
  };

  $: itemProducts = $makeupLibrary.map((m) => m.records).flat();
  $: makeups = objectify($supplier?.makeups || []);
  $: products = objectify($supplier?.products || []);
  $: productSettings = objectify(
    $supplier?.products.filter((p) => p.application === "product_setting") || [],
  );
  $: paramsObj = objectify(params, "key");
  $: settings = $group?.data.settings;
  $: sections = category
    ? $makeupLibrary?.filter((s) => s.title === category || s.id === category)
    : $makeupLibrary;
  $: filteredProducts = makeFP(sections, filter, $supplier);
  $: updateSpecOptions(specs);
  $: usedTypes = $group.items.order.reduce((a, id) => {
    const item = $group.items[id];
    if (item?.type_id) {
      if (!a[item.type_id]) {
        a[item.type_id] = [];
      }

      a[item?.type_id].push(id);
    }
    return a;
  }, {});
  $: checkForProductUpdates(params);
  $: itemPreview = makeItemPreview(newItem, itemProductEntry, params, product);
  $: productOptions = getProductOptions(itemProductEntry, makeups);

  function makeFP(sections, filter, supplier) {
    const categories = [];
    const uncategorized = [];
    sections?.forEach((section) => {
      if (section.is_uncategorized) {
        uncategorized.push(...section.records.filter(recordFilter(filter)));
      } else {
        const category = supplier?.product_categories[section.id];

        const records = section.records.filter(recordFilter(filter));
        if (records.length === 1) {
          categories.push(records[0]);
        } else {
          categories.push({
            ...section,
            image: category?.image,
            is_category: true,
            records: section.records.filter(recordFilter(filter)),
          });
        }
      }
    });

    return {
      records: [...categories, ...uncategorized],
    };
  }

  function getProductOptions(itemProductEntry, makeups) {
    if (!itemProductEntry?._orig_data?.makeup?.options) return [];
    return itemProductEntry._orig_data.makeup.options.map((id) => makeups[id]).filter((p) => p);
  }

  function openModal(prop) {
    filter = "";
    newItem = null;
    editing = false;
    product = null;
    itemProductEntry = null;
    selectedCategory = null;
    specs = [];
    params = [];
    if (prop?.category) {
      category = prop.category;
    } else {
      category = null;
    }

    if (prop?.item) {
      newItem = cloneDeep($group.items[prop.item]);
      if (!newItem) return;
      const type = $group.types[newItem.type_id];
      if (!type) return;

      editing = true;
      editedItemId = newItem.id;
      editedTypeId = type.id;
      itemProductEntry = itemProducts.find((p) => p.id === type.product_id);
      product = cloneDeep(type);
      if (itemProductEntry?.category_id) {
        selectedCategory = filteredProducts.records.find((r) => r.id === itemProductEntry.category_id);
      }
      if (itemProductEntry?.item_script) {
        const pm = newItem.data.meta_parameters || {};
        params = getParameters(itemProductEntry).map((p) => {
          let value = p.value;
          if (pm[p.key] !== undefined) {
            value = pm[p.key];
          }

          if (p.type === "dimtext" && value) {
            value = new Dimension(value);
          }

          return {
            ...p,
            value,
          };
        });
      } else {
        specs = productSpecs(product).slice(-1);
        if (!specs.length) {
          selectedProductId = itemProductEntry?.id;
          itemProductEntry = null;
          product = null;
          newItem = null;
        } else {
          selectedProductId = specs[0].makeup.id;
        }
      }
    }

    modal.open();
  }

  function isParamPresent(param, paramsObj) {
    if (!param || !paramsObj) return true;
    if (!param.condition) return true;

    let keep = false;
    Object.keys(param.condition).forEach((key) => {
      const condition = param.condition[key];
      const p = paramsObj[key];
      if (condition.operator === "is" && p?.value === condition.value) {
        keep = true;
      } else if (condition.operator === "is_not" && p?.value !== condition.value) {
        keep = true;
      } else if (
        condition.operator === "is_one_of" &&
        Array.isArray(condition.value) &&
        condition.value.includes(p?.value)
      ) {
        keep = true;
      } else if (
        condition.operator === "is_not_one_of" &&
        Array.isArray(condition.value) &&
        !condition.value.includes(p?.value)
      ) {
        keep = true;
      }
    });
    return keep;
  }

  async function updateSpecOptions(specs) {
    specOptions = [];
    if (specs.length) {
      const options = await api.rpc("product_query_options", { makeup: specs[0].makeup });
      if (options.data) {
        specOptions = options.data.map((d) => {
          if (d.application === "material") hydrateMaterial(d);
          return d;
        });

        if (specOptions.length === 1) {
          selectSpec(specOptions[0]);
        }
      }
    }
  }

  function updateFabs(newFabs, fabs, productFabs) {
    fabs?.forEach((f) => {
      if (f.product_id) {
        if (productFabs[f.product_id]) {
          f.fab_id = productFabs[f.product_id];
        } else {
          newFabs[f.fab_id] = f.fabrication;
        }
      }
    });
  }

  function getNewFabs(item, group) {
    const fabs = {};
    const productFabs = {};

    group.data.fabrications?.order.forEach((id) => {
      const fab = group.data.fabrications[id];
      if (fab.product_id) {
        productFabs[fab.product_id] = id;
      }
    });

    updateFabs(fabs, item.data.fabrications?.edge, productFabs);
    updateFabs(fabs, item.data.fabrications?.voids, productFabs);
    updateFabs(fabs, item.data.fabrications?.corner, productFabs);
    updateFabs(fabs, item.data.fabrications?.virtual, productFabs);
    updateFabs(fabs, item.data.fabrications?.additive, productFabs);

    return Object.values(fabs);
  }

  function selectType(p) {
    itemProductEntry = p;

    product = hydrateType({
      ...defaultType($group.id, types),
      ...pick(p, [
        "description",
        "name",
        "image",
        "category",
        "category_id",
        "item_template",
        "item_template_mask",
        "use_item_drawing_as_thumbnail",
        "use_type_image_as_thumbnail",
        "pricing_group",
        "freight_group",
      ]),
      data: cloneDeep(p.data),
      product_spec: p._orig_data,
      product_id: p.id,
    });

    const hasScript = p.item_script && !isEmpty(p.item_script_decl?.input);

    if (p.defer_product_selection_to_script && hasScript) {
      specs = [];
    } else {
      specs = productSpecs(product);
    }

    if (!specs.length) {
      const item = {
        ...defaultItem($group.id, $profile.id, items, types, { type_id: product.id }),
        ...product.item_template,
      };

      if (item.shape.type === "none") {
        item.mark = null;
      }

      if (hasScript) {
        newItem = item;
        params = getParameters(p) || [];
        return;
      }

      if (editing) {
        const { id, cache, approver, updater, ...diff } = product;
        dispatch("updateType", { type_id: editedTypeId, diff });
      } else {
        const existingType = types.find((t) => {
          return p.id === t.product_id && usedTypes[t.id];
        });

        if (existingType) {
          item.type_id = existingType.id;

          const existingItem = items.find((i) => i.type_id === existingType.id && !i.data.meta_parameters);
          dispatch("addProduct", { item_id: existingItem?.id, type_id: existingType.id, product, item });
        } else {
          dispatch("addProduct", { product, item });
        }
      }

      modal.close();
    }
  }

  function getParameters(p) {
    const decl = p.item_script_decl;
    const parsed = parseItemParams(decl.input);
    const productParam = parsed.find((param) => param.type === "product");
    if (productParam) {
      if (p._orig_data?.makeup.value) {
        productParam.value = p._orig_data.makeup.value;
      }
    }

    return parsed;
  }

  function cancelParams() {
    modal.close();
  }

  function paramsAreEqual(a, b) {
    let def;
    if (Array.isArray(a) || Array.isArray(b)) {
      def = [];
    } else {
      def = {};
    }

    const a0 = JSON.parse(JSON.stringify(a || def));
    const b0 = JSON.parse(JSON.stringify(b || def));
    return isEqual(a0, b0);
  }

  function updateProduct(params) {
    const category_id = product?.category_id;
    const category = product?.category;
    params.forEach((param) => {
      const prodSpec = param.path.match(
        /product\.data\.layers\[(\d+)\]\.(inboard_surface|material|outboard_surface)\.id/,
      );
      if (param.path === "product" && makeups[param.value]) {
        const {
          category,
          category_id,
          item_template,
          item_template_mask,
          use_item_drawing_as_thumbnail,
          use_type_image_as_thumbnail,
          pricing_group,
          freight_group,
        } = product;

        const p = makeups[param.value];
        product = hydrateType({
          ...defaultType($group.id, types),
          ...pick(p, ["description", "name", "image"]),
          category,
          category_id,
          item_template,
          item_template_mask,
          use_item_drawing_as_thumbnail,
          use_type_image_as_thumbnail,
          pricing_group,
          freight_group,
          data: cloneDeep(p.expanded_data),
          product_spec: p.data,
          product_id: product?.product_id,
        });
      } else if (prodSpec) {
        const layer = prodSpec[1];
        const prod = prodSpec[2];

        const newProd = products[param.value];
        const path = product.data.layers?.[layer]?.[prod];

        if (path !== undefined && param.value === null) {
          product.data.layers[layer][prod] = null;
        } else if (newProd && path !== undefined) {
          let updated = {
            ...product.data.layers[layer][prod],
            ...newProd,
          };

          if (prod === "material") updated = hydrateMaterial(updated);

          product.data.layers[layer][prod] = updated;
        } else {
          if (!newProd) {
            console.warn("Unable to find product", param.value);
          }

          if (path === undefined) {
            console.warn("Invalid path", `layer ${layer}`, `product ${prod}`);
          }
        }
      } else {
        set(product, param.path, param.value);
      }
    });

    product.data.meta_parameters = params;
    product = product;
  }

  function runScript(newItem, itemProductEntry, params, product) {
    if (!newItem || !itemProductEntry || !params || !product) return null;
    if (!itemProductEntry.item_script) return null;
    if (params.some((p) => p.value === undefined)) return null;

    const inputParams = params.reduce((a, p) => {
      a[p.key] = p.value;
      return a;
    }, {});

    const context = { group: $group, supplier: $supplier, settings: productSettings };
    const script = parseItemScript(itemProductEntry.item_script);
    return script.run(newItem, inputParams, context);
  }

  function checkForProductUpdates(params) {
    const result = runScript(newItem, itemProductEntry, params, product);
    if (!result) return;

    const { type } = result;
    if (type) {
      updateProduct(type.params);
      newItem.type_id = product.id;
    }
  }

  function makeItemPreview(newItem, itemProductEntry, params, product) {
    const result = runScript(newItem, itemProductEntry, params, product);
    if (!result) return null;
    const { item, type } = result;
    const hydrated = hydrateItem(item, $group);
    const typeColor = typeColorTrue(product);
    return liteDrawing(hydrated, { typeColor, showMark: false, showDims: false, showOffsets: true });
  }

  function addProductWithParams() {
    const inputParams = params.reduce((a, p) => {
      a[p.key] = p.value;
      return a;
    }, {});

    const context = { group: $group, supplier: $supplier, settings: productSettings };
    const script = parseItemScript(itemProductEntry.item_script);
    const { item, type } = script.run(newItem, inputParams, context);
    item.data.meta_parameters = inputParams;
    if (type) {
      updateProduct(type.params);
      item.type_id = product.id;
    }

    const newFabs = getNewFabs(item, $group);

    const ps = productSpecs(product);
    const existingType = types.find((t) => {
      if (t.product_id !== product.product_id) return false;
      if (!usedTypes[t.id]) return false;
      if (!paramsAreEqual(t.data.meta_parameters, product.data.meta_parameters)) return false;

      const specsEqual = ps.every((s) => {
        const np = product.data.layers[s.index]?.[s.path];
        const tp = t.data.layers?.[s.index]?.[s.path];
        return np?.id === tp?.id;
      });

      if (!specsEqual) return false;

      return usedTypes[t.id].some((itemId) => {
        const tItem = $group.items[itemId];
        return paramsAreEqual(tItem.data.meta_parameters, item.data.meta_parameters);
      });
    });

    if (existingType?.id) {
      item.type_id = existingType.id;

      const existingItem = items.find((i) => {
        if (i.type_id !== item.type_id) return false;
        return paramsAreEqual(i.data.meta_parameters, item.data.meta_parameters);
      });

      dispatch("addProduct", {
        item_id: existingItem?.id,
        type_id: item.type_id,
        product,
        fabrications: newFabs,
        item,
      });
    } else {
      item.type_id = product.id;
      dispatch("addProduct", { product, item, fabrications: newFabs });
    }
    modal.close();
  }

  function updateProductWithParams() {
    const inputParams = params.reduce((a, p) => {
      a[p.key] = p.value;
      return a;
    }, {});

    const context = { group: $group, supplier: $supplier, settings: productSettings };
    const script = parseItemScript(itemProductEntry.item_script);
    const { item, type } = script.run(newItem, inputParams, context);
    item.data.meta_parameters = inputParams;
    if (type) {
      updateProduct(type.params);
    }

    const newFabs = getNewFabs(item, $group);

    const { id: tid, cache: tcache, approver: tapprover, updater: tupdater, ...type_diff } = product;
    const { id, cache, approver, updater, type_id, ...item_diff } = item;

    dispatch("updateProduct", {
      type_id: editedTypeId,
      type_diff,
      item_id: editedItemId,
      item_diff,
      fabrications: newFabs,
    });

    modal.close();
  }

  function resetItemProduct() {
    product = null;
    itemProductEntry = null;
    newItem = null;
  }

  function selectSpec(record) {
    const s = specs[0];
    const layer = product.data.layers[s.index];
    layer[s.path] = {
      ...layer[s.path],
      ...record,
    };

    specs = specs.slice(1);

    if (!specs.length && editing) {
      const { id, cache, approver, updater, ...diff } = product;
      dispatch("updateType", { type_id: editedTypeId, diff });
      modal.close();
    } else if (!specs.length) {
      const ps = productSpecs(product);
      const existing = types.find((t) => {
        if (t.product_id !== product.product_id) return false;

        return ps.every((s) => {
          const np = product.data.layers[s.index]?.[s.path];
          const tp = t.data.layers?.[s.index]?.[s.path];
          return np?.id === tp?.id && usedTypes[t.id];
        });
      });

      const item = {
        ...defaultItem($group.id, $profile.id, items, types, { type_id: existing?.id || product.id }),
        ...product.item_template,
      };

      if (item.shape.type === "none") {
        item.mark = null;
      }

      if (itemProductEntry.item_script && !isEmpty(itemProductEntry.item_script_decl?.input)) {
        newItem = item;
        params = getParameters(itemProductEntry) || [];
        return;
      }

      if (existing) {
        const existingItem = items.find((i) => i.type_id === existing.id && !i.data.meta_parameters);
        dispatch("addProduct", { item_id: existingItem?.id, type_id: existing.id, product, item });
      } else {
        dispatch("addProduct", { product, item });
      }
      modal.close();
    }
  }

  function selectCategory(e) {
    if (e.detail.record?.is_category) {
      selectedCategory = e.detail.record;
    } else {
      selectType(e.detail.record);
    }
  }

  function updateSupplier(e) {
    dispatch("updateSupplier", e.detail);
  }
</script>

<Modal width="36rem" bind:this={modal} closeable fullframe>
  <div slot="title">
    {#if editing}
      Edit Product
    {:else}
      Add Product
    {/if}
  </div>
  <div slot="content" class="overflow-y-auto">
    <div class="p-4 space-y-2 text-xs">
      {#if !product}
        <div class="mb-4">
          <FilterInput bind:filter />
        </div>
        {#if $suppliers?.length > 1}
          <div>Choose a supplier:</div>
          <SelectInput
            border
            label="Supplier"
            value={$supplier?.id}
            options={$suppliers.map((s) => ({ value: s.id, label: s.name }))}
            on:input={updateSupplier} />
        {/if}
        {#if itemProducts.length}
          {#if !selectedCategory}
            <LibrarySection
              section={filteredProducts}
              on:select={selectCategory}
              selected={selectedProductId}>
              <div slot="thumbnail" class="relative w-full h-full" let:record>
                {#if record.image}
                  <img
                    class="w-full h-full object-cover text-xs"
                    src={imageUrl(record.image)}
                    alt={record.title} />
                {/if}
              </div>
              <div slot="caption" let:record>
                {#if record.is_category}
                  <div>
                    {record.title}
                  </div>
                  <div class="italic">{record.records.length} products</div>
                {:else}
                  <div>
                    {record?.name}
                  </div>
                {/if}
              </div>
            </LibrarySection>
          {:else}
            <button class="btn-text flex items-center gap-1" on:click={() => (selectedCategory = null)}>
              <CaretLeftIcon />
              <div>All Products</div>
            </button>
            <LibrarySection
              section={selectedCategory}
              selected={selectedProductId}
              on:select={(e) => selectType(e.detail.record)}
              collapsible={false}>
              <MakeupThumbnail slot="thumbnail" let:record {record} />
              <div slot="caption" let:record>{record?.name}</div>
            </LibrarySection>
          {/if}
        {:else}
          <div class="italic">No products</div>
        {/if}
      {:else if specs.length}
        {@const spec = specs[0]}
        <button class="btn-text flex gap-2 items-center" on:click={resetItemProduct}>
          <CaretLeftIcon />
          <div>Select Product</div>
        </button>
        <div class="w-32 rounded border border-gray-300 bg-white p-2 shadow">
          <div class="w-full border relative aspect-square thumbnail-image">
            <MakeupThumbnail record={product} />
          </div>
          <div class="text-xxs">
            {product.name}
          </div>
        </div>
        <div class="pt-2">
          Choose {spec.name}:
        </div>
        <LibrarySection
          section={{ records: specOptions }}
          on:select={(e) => selectSpec(e.detail.record)}
          selected={selectedProductId}>
          <div slot="thumbnail" let:record class="w-full h-full relative">
            {#if record.image}
              <img
                src={imageUrl(record.image)}
                alt={record.name}
                class="text-xs w-full h-full object-cover" />
            {:else if record.application === "material"}
              <ViewportMinimal padding={8} drawing={materialDrawing(record)} />
            {:else}
              <div class="w-full h-full" style="background-color: {record?.color || '#FFFFFF'}"></div>
            {/if}
          </div>
          <div slot="caption" let:record>
            {record.name}
          </div>
        </LibrarySection>
      {:else if params.length}
        <button class="btn-text flex gap-2 items-center" on:click={resetItemProduct}>
          <CaretLeftIcon />
          <div>Select Product</div>
        </button>
        <div class="flex gap-2 items-center">
          <div class="flex-none relative w-64 h-64">
            {#if itemPreview}
              <Viewport padding={16} drawing={itemPreview} width={256} height={256} />
            {:else}
              <div class="w-full h-full bg-gray-100 flex items-center justify-center">
                <div class="italic text-xs">No preview</div>
              </div>
            {/if}
          </div>
          <div class="grow w-48 space-y-2">
            {#each params as param}
              {@const present = isParamPresent(param, paramsObj)}
              {#if present}
                {#if param.type === "product"}
                  <ProductInput
                    border
                    labelWidth="8rem"
                    label={param.label}
                    {group}
                    {types}
                    bind:product
                    bind:value={param.value}
                    options={productOptions} />
                {:else}
                  <svelte:component
                    this={inputs[param.type]}
                    border
                    labelWidth="8rem"
                    label={param.label}
                    bind:value={param.value}
                    options={param.options}
                    min={param.min}
                    max={param.max}
                    selectionType="native"
                    {settings} />
                {/if}
              {/if}
            {/each}
          </div>
        </div>
        <div class="flex justify-end gap-4">
          <button class="btn btn-cancel" on:click={cancelParams}> Cancel </button>
          {#if editing}
            <button
              class="btn btn-primary"
              on:click={updateProductWithParams}
              disabled={params.some((p) => p.value === undefined)}>
              Update Product
            </button>
          {:else}
            <button
              class="btn btn-primary"
              on:click={addProductWithParams}
              disabled={params.some((p) => p.value === undefined)}>
              Add Product
            </button>
          {/if}
        </div>
      {/if}
    </div>
  </div>
</Modal>

<style lang="scss">
  .thumbnail-image {
    min-height: 5rem;
  }
</style>
